import * as THREE from 'three';

export type ColliderKind = 'box' | 'ramp' | 'platform';

/** Platform bergerak: slab AABB axis-aligned yang digeser di sumbu X/Z
 *  (ping-pong). Collider min/max dimekarkan tiap frame oleh stepPlatforms;
 *  fisika player/crate memakainya persis seperti box statis. */
export interface PlatformSpec {
  x: number;
  z: number;
  w: number;
  d: number;
  h: number;
  /** Ketinggian permukaan atas platform (dunia). */
  y: number;
  axis: 'x' | 'z';
  /** Jarak tempuh satu arah (total lintasan 2x span per periode). */
  span: number;
  /** Detik untuk satu siklus bolak-balik penuh. */
  period: number;
  /** Fase 0..1 (0 = mulai di ujung +span/2 dari base). */
  phase?: number;
  /** Mode gerak: 'pingpong' (default) atau 'chase' (sapuan satu arah +Z
   *  untuk dinding pengejar). Saat chase, span/period/phase diabaikan. */
  mode?: 'pingpong' | 'chase';
  /** [mode chase] Koordinat Z akhir sapuan (berhenti di sini). */
  chaseEndZ?: number;
  /** [mode chase] Kecepatan sapuan (m/detik). */
  chaseSpeed?: number;
  /** [mode chase] Mulai false (berhenti) sampai runtime menyalakannya. */
  chaseStarted?: boolean;
  /** [mode chase] True = tahan sapuan (ada crate terjepit di depan:
   *  maju terus = memompa crate tembus collider). Diset runtime. */
  chaseBlocked?: boolean;
}

export interface MovingPlatform {
  spec: PlatformSpec;
  collider: ColliderDef;
  mesh: THREE.Object3D;
  px: number;
  pz: number;
  clock: number;
}

/** Entity yang bisa diangkut platform (player & crate). */
export interface PlatformSupport {
  x: number;
  z: number;
  y: number;
  apply(dx: number, dz: number): void;
}

export interface ColliderDef {
  object: THREE.Object3D;
  kind: ColliderKind;
  min: THREE.Vector3;
  max: THREE.Vector3;
  tiltX: number;
  tiltZ: number;
  rampStartY: number;
  rampEndY: number;
  /** Nonaktif (mis. gate dibuka): dilewati seluruh query fisika. */
  disabled?: boolean;
}

export interface SurfaceSample {
  valid: boolean;
  height: number;
  tiltX: number;
  tiltZ: number;
}

/** Zona air (sungai): visual di atas lantai biasa; crate mengapung secara
 *  visual (fisika crate tetap di y=0), pemain yang menyentuh air respawn
 *  ke titik aman terakhir (kecuali berdiri di atas crate/objek). */
export interface WaterRect {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Ketinggian permukaan air (visual). */
  y: number;
}

/** Lubang cekung (parit sungai): tanah turun ke `depth`. Tanpa collider —
 *  entity jatuh ke dasar; pemain yang menyentuh air di atasnya respawn. */
export interface PitRect {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Ketinggian dasar lubang (di bawah lantai 0). */
  depth: number;
}

/** Status timer sentuhan air: -1 = kering/bebas. */
export interface WaterTimerState {
  timer: number;
  /** True tepat di frame pertama menyentuh air (mainkan cipratan). */
  splash: boolean;
  /** True saat jeda habis → respawn sekarang. */
  respawn: boolean;
}

/** Jeda sebelum respawn air (detik): sentuh -> tenggelam sesaat -> balik. */
export const WATER_RESPAWN_DELAY = 0.25;

/** Hitung mundur sentuhan air. Murni (testable); scene mengeksekusi
 *  splash/resetTo. `touching` = kaki di bawah permukaan + 0.1. */
export function waterTimerTick(timer: number, touching: boolean, delta: number): WaterTimerState {
  if (!touching) return { timer: -1, splash: false, respawn: false };
  if (timer < 0) return { timer: WATER_RESPAWN_DELAY, splash: true, respawn: false };
  const t = timer - delta;
  if (t <= 0) return { timer: -1, splash: false, respawn: true };
  return { timer: t, splash: false, respawn: false };
}

export interface WallContact {
  x: number;
  y: number;
  z: number;
  normalX: number;
  normalY: number;
  normalZ: number;
}

/** Offset platform dari base di waktu u (fase 0..1): gelombang segitiga. */
function platformOffset(u: number, span: number): number {
  const uu = ((u % 1) + 1) % 1;
  const tri = 4 * Math.abs(uu - 0.5) - 1;
  return (tri * span) / 2;
}

export class CollisionWorld {
  readonly colliders: ColliderDef[] = [];
  readonly meshes: THREE.Object3D[] = [];
  private platforms: MovingPlatform[] = [];
  readonly waters: WaterRect[] = [];
  readonly pits: PitRect[] = [];

  /** Kosongkan semua collider (untuk ganti stage). Mesh dihapus pemanggil. */
  clear(): void {
    this.colliders.length = 0;
    this.meshes.length = 0;
    this.platforms.length = 0;
    this.waters.length = 0;
    this.pits.length = 0;
  }

  get platformList(): readonly MovingPlatform[] {
    return this.platforms;
  }

  /** Aktif/nonaktifkan collider berdasarkan object mesh-nya (gate). */
  setColliderEnabled(object: THREE.Object3D, enabled: boolean): void {
    for (const c of this.colliders) {
      if (c.object === object) c.disabled = !enabled;
    }
  }

  /** Daftarkan zona air (tanpa collider: hanya query visual/respawn). */
  addWater(x: number, z: number, w: number, d: number, y: number): void {
    this.waters.push({
      minX: x - w / 2,
      maxX: x + w / 2,
      minZ: z - d / 2,
      maxZ: z + d / 2,
      y,
    });
  }

  /** Daftarkan lubang cekung (tanah turun; crate jatuh ke dasar). */
  addPit(x: number, z: number, w: number, d: number, depth: number): void {
    this.pits.push({
      minX: x - w / 2,
      maxX: x + w / 2,
      minZ: z - d / 2,
      maxZ: z + d / 2,
      depth,
    });
  }

  /** Ketinggian permukaan air di (x,z), atau null bila kering. */
  waterAt(x: number, z: number): number | null {
    for (const wr of this.waters) {
      if (x >= wr.minX && x <= wr.maxX && z >= wr.minZ && z <= wr.maxZ) return wr.y;
    }
    return null;
  }

  /** Daftar platform bergerak stage ini (dipanggil saat build layout). */
  addPlatform(spec: PlatformSpec): MovingPlatform {
    // Clone: runtime memutasi flag chase (chaseStarted/chaseBlocked);
    // tanpa clone menempel ke def stage shared lintas rebuild/retry.
    spec = { ...spec };
    const offset = platformOffset(spec.phase ?? 0.5, spec.span);
    const px = spec.x + (spec.axis === 'x' ? offset : 0);
    const pz = spec.z + (spec.axis === 'z' ? offset : 0);
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(spec.w, spec.h, spec.d),
      new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.7 }),
    );
    mesh.position.set(px, spec.y - spec.h / 2, pz);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    const collider: ColliderDef = {
      object: mesh,
      kind: 'platform',
      min: new THREE.Vector3(px - spec.w / 2, spec.y - spec.h, pz - spec.d / 2),
      max: new THREE.Vector3(px + spec.w / 2, spec.y, pz + spec.d / 2),
      tiltX: 0,
      tiltZ: 0,
      rampStartY: spec.y,
      rampEndY: spec.y,
    };
    this.colliders.push(collider);
    this.meshes.push(mesh);

    const p: MovingPlatform = {
      spec,
      collider,
      mesh,
      px,
      pz,
      clock: 0,
    };
    this.platforms.push(p);
    return p;
  }

  /** Majukan platform oleh delta detik. Entity yang bertumpu dibawa serta
   *  (carry dua-tahap: delta dihitung dari posisi lama -> diterapkan ->
   *  baru collider dipindah, mencegah jitter/mikro-bounce). */
  stepPlatforms(delta: number, supports: PlatformSupport[]): void {
    for (const p of this.platforms) {
      if (p.spec.mode === 'chase') {
        // Dinding pengejar: sapuan satu arah +Z, berhenti di chaseEndZ.
        // Diam saat belum dinyalakan ATAU saat ditahan (anti-crush:
        // crate terjepit di depan -> maju = pompa tembus).
        if (!p.spec.chaseStarted || p.spec.chaseBlocked) continue;
        const speed = p.spec.chaseSpeed ?? 1;
        const endZ = p.spec.chaseEndZ ?? p.pz;
        const nz = Math.min(p.pz + speed * delta, endZ);
        const dx = 0;
        const dz = nz - p.pz;
        if (dz === 0) continue;
        const { w, d, y } = p.spec;
        const margin = 0.3;
        for (const s of supports) {
          if (
            Math.abs(s.x - p.px) <= w / 2 - margin &&
            Math.abs(s.z - p.pz) <= d / 2 - margin &&
            Math.abs(s.y - y) < 0.22
          ) {
            s.apply(dx, dz);
          }
        }
        p.pz = nz;
        p.collider.min.z = nz - d / 2;
        p.collider.max.z = nz + d / 2;
        p.mesh.position.z = nz;
        continue;
      }
      p.clock += delta;
      const u0 = p.spec.phase ?? 0.5;
      const u = (((p.clock / p.spec.period + u0) % 1) + 1) % 1;
      const offset = platformOffset(u, p.spec.span);
      const nx = p.spec.x + (p.spec.axis === 'x' ? offset : 0);
      const nz = p.spec.z + (p.spec.axis === 'z' ? offset : 0);
      const dx = nx - p.px;
      const dz = nz - p.pz;
      const { w, d, y } = p.spec;
      const margin = 0.3;
      for (const s of supports) {
        if (
          Math.abs(s.x - p.px) <= w / 2 - margin &&
          Math.abs(s.z - p.pz) <= d / 2 - margin &&
          Math.abs(s.y - y) < 0.22
        ) {
          s.apply(dx, dz);
        }
      }
      p.px = nx;
      p.pz = nz;
      p.collider.min.x = nx - w / 2;
      p.collider.max.x = nx + w / 2;
      p.collider.min.z = nz - d / 2;
      p.collider.max.z = nz + d / 2;
      p.mesh.position.x = nx;
      p.mesh.position.z = nz;
    }
  }

  addBox(
    x: number,
    y: number,
    z: number,
    w: number,
    h: number,
    d: number,
    color: number,
  ): THREE.Object3D {
    const geo = new THREE.BoxGeometry(w, h, d);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.7 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    this.colliders.push({
      object: mesh,
      kind: 'box',
      min: new THREE.Vector3(x - w / 2, y - h / 2, z - d / 2),
      max: new THREE.Vector3(x + w / 2, y + h / 2, z + d / 2),
      tiltX: 0,
      tiltZ: 0,
      rampStartY: 0,
      rampEndY: 0,
    });
    this.meshes.push(mesh);
    return mesh;
  }

  addRamp(
    x: number,
    z: number,
    w: number,
    d: number,
    lowY: number,
    highY: number,
    color: number,
  ): THREE.Object3D {
    const h = 0.35;
    const geo = new THREE.BoxGeometry(w, h, d);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.7 });
    const mesh = new THREE.Mesh(geo, mat);

    const midY = (lowY + highY) / 2;
    const angle = Math.atan2(highY - lowY, d);
    mesh.position.set(x, midY - (h / 2) * Math.cos(angle), z);
    mesh.rotation.x = -angle;
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    const tiltZ = (highY - lowY) / d;
    this.colliders.push({
      object: mesh,
      kind: 'ramp',
      min: new THREE.Vector3(x - w / 2, Math.min(lowY, highY) - 0.1, z - d / 2),
      max: new THREE.Vector3(x + w / 2, Math.max(lowY, highY) + 0.1, z + d / 2),
      tiltX: 0,
      tiltZ,
      rampStartY: lowY,
      rampEndY: highY,
    });
    this.meshes.push(mesh);
    return mesh;
  }

  /** Tinggi permukaan tertinggi di (x,z). Bila `headHeight` diberikan,
   *  collider yang badannya SEPENUHNYA di atas kepala diabaikan (cekung
   *  "langit-langit") -> entity di bawah platform melayang tidak pernah
   *  ter-snap ke atasnya (mencegah 'naik sendiri dari bawah'). */
  sampleGroundHeight(x: number, z: number, headHeight?: number): SurfaceSample {
    let best: SurfaceSample = { valid: false, height: 0, tiltX: 0, tiltZ: 0 };

    for (const c of this.colliders) {
      if (c.disabled) continue;
      if (x < c.min.x || x > c.max.x || z < c.min.z || z > c.max.z) continue;
      if (headHeight !== undefined && c.min.y >= headHeight) continue;

      if (c.kind === 'box' || c.kind === 'platform') {
        const height = c.max.y;
        if (!best.valid || height > best.height) {
          best = { valid: true, height, tiltX: 0, tiltZ: 0 };
        }
      } else {
        const height = c.rampStartY + c.tiltZ * (z - c.min.z);
        if (!best.valid || height > best.height) {
          best = { valid: true, height, tiltX: c.tiltX, tiltZ: c.tiltZ };
        }
      }
    }

    // Lubang cekung: bila tak ada permukaan collider di atas dasar lubang,
    // tanah = dasar (entity jatuh ke dalam; crate terparkir di dasar).
    for (const p of this.pits) {
      if (x < p.minX || x > p.maxX || z < p.minZ || z > p.maxZ) continue;
      if (!best.valid || best.height < p.depth) {
        best = { valid: true, height: p.depth, tiltX: 0, tiltZ: 0 };
      }
    }

    return best;
  }

  sampleWall(
    x: number,
    y: number,
    z: number,
    radius: number,
    height: number,
  ): WallContact | null {
    let nearest: WallContact | null = null;
    let nearestDist = Infinity;

    for (const c of this.colliders) {
      if (c.disabled || c.kind !== 'box') continue;

      const feet = y;
      const head = y + height;
      if (head <= c.min.y || feet >= c.max.y) continue;

      const cx = THREE.MathUtils.clamp(x, c.min.x, c.max.x);
      const cz = THREE.MathUtils.clamp(z, c.min.z, c.max.z);
      const dx = x - cx;
      const dz = z - cz;
      const dist = Math.hypot(dx, dz);
      // Toleransi float: pemain yang menempel PERSIS di dinding (dist ==
      // radius) bisa terhitung 0.5500000000000007 > 0.55 -> kontak hilang,
      // jejak dinding tak pernah muncul. Selisih < 1mm dianggap sentuh.
      if (dist > radius + 1e-3) continue;

      const bodyCenter = (feet + head) / 2;
      const wallY = Math.min(Math.max(bodyCenter, c.min.y + 0.3), c.max.y - 0.3);

      const penX = Math.min(x - c.min.x, c.max.x - x);
      const penZ = Math.min(z - c.min.z, c.max.z - z);

      let nx = 0;
      let nz = 0;
      let wx = x;
      let wz = z;
      if (penX < penZ) {
        nx = x - c.min.x < c.max.x - x ? -1 : 1;
        wx = nx < 0 ? c.min.x : c.max.x;
      } else {
        nz = z - c.min.z < c.max.z - z ? -1 : 1;
        wz = nz < 0 ? c.min.z : c.max.z;
      }

      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = { x: wx, y: wallY, z: wz, normalX: nx, normalY: 0, normalZ: nz };
      }
    }

    return nearest;
  }

  /** Kaki sedang berpijak DI ATAS lereng (bukan di bawah/di sampingnya)?
   *  Syarat ketat: kaki terangkat (>0.05) DAN permukaan ramp lain dalam
   *  ±0.6 m dari kaki. Tanpa ini, pemain di tanah di bawah lereng tinggi
   *  bisa jalan MENEMBUS sisi box tetangga (hanya satu arah: dari sisi
   *  ramp) — karena "ada permukaan di atas kaki" disalahartikan sebagai
   *  sanggahan. Flat-ke-flat tetap strict (lompat untuk naik, sesuai
   *  desain maxStepHeight). */
  private feetOnRamp(x: number, z: number, feet: number, skip: ColliderDef): boolean {
    if (feet <= 0.05) return false;
    for (const other of this.colliders) {
      if (other === skip || other.disabled || other.kind !== 'ramp') continue;
      if (x < other.min.x || x > other.max.x || z < other.min.z || z > other.max.z) continue;
      const sY =
        other.rampStartY + other.tiltZ * (z - other.min.z) + other.tiltX * (x - other.min.x);
      if (Math.abs(sY - feet) <= 0.6) return true;
    }
    return false;
  }

  resolveAABB(
    position: THREE.Vector3,
    radius: number,
    height: number,
    hVel?: { x: number; z: number },
  ): { resolved: THREE.Vector3; blocked: boolean } {
    const resolved = position.clone();
    let blocked = false;
    const x = resolved.x;
    const z = resolved.z;

    for (const c of this.colliders) {
      if (c.disabled) continue;
      const isRamp = c.kind === 'ramp';

      const closestX = THREE.MathUtils.clamp(resolved.x, c.min.x, c.max.x);
      const closestZ = THREE.MathUtils.clamp(resolved.z, c.min.z, c.max.z);
      const dx = resolved.x - closestX;
      const dz = resolved.z - closestZ;
      const distSq = dx * dx + dz * dz;
      if (distSq >= radius * radius) continue;

      const feet = resolved.y;
      const head = resolved.y + height;

      const surfaceY = isRamp
        ? c.rampStartY + c.tiltZ * (closestZ - c.min.z) + c.tiltX * (closestX - c.min.x)
        : c.max.y;

      if (feet >= surfaceY) continue;
      if (head <= c.min.y) continue;

      // Ramp di bawah kaki (permukaan lain yang menyangga): jangan biarkan
      // box pedestal yang menyentuh lereng (tepat di kaki ramp) dianggap
      // dinding — slime yang menanjak lereng menyambung ke permukaan datar
      // TIDAK boleh 'nyangkut' di sisi box. Syarat: ada collider lain yang
      // surface-nya >= feet dan berada di bawah/sekitar posisi ini.
      if (!isRamp && this.feetOnRamp(x, z, feet, c)) continue;

      // Pembeda "jalan lepas dari tepi" vs "menabrak dinding": arah gerak
      // relatif ke muka box. Character yang sedang MENJAUH/DIAM di bibir
      // (dari atas tepi) tidak boleh didorong (snap-radius di bibir =
      // snap ke depan); yang MENDATANGI harus diblokir — kalau tidak,
      // box rendah overlap -> sampleGroundHeight men-snap kaki ke atas
      // box = bisa "naik tanpa lompat".
      let nxOut = 0;
      let nzOut = 0;
      if (resolved.x <= c.min.x + 1e-6) nxOut = -1;
      else if (resolved.x >= c.max.x - 1e-6) nxOut = 1;
      if (resolved.z <= c.min.z + 1e-6) nzOut = -1;
      else if (resolved.z >= c.max.z - 1e-6) nzOut = 1;
      if (nxOut !== 0 || nzOut !== 0) {
        const sep = hVel ? hVel.x * nxOut + hVel.z * nzOut : 0;
        if (feet >= surfaceY - 0.5) {
          // Zona bibir (0.5m di bawah permukaan): hanya mendekat yang
          // didorong. Menjauh (sep>0) atau diam (sep==0) = biarkan lepas
          // dari tepi.
          if (sep >= 0) continue;
        } else if (sep > 0.05) {
          // Jauh di bawah bibir: hanya jelas-menjauh yang dilewati.
          continue;
        }
      }

      blocked = true;
      const dist = Math.sqrt(distSq);
      if (dist > 1e-4) {
        const push = radius - dist;
        resolved.x += (dx / dist) * push;
        resolved.z += (dz / dist) * push;
      }
    }

    return { resolved, blocked };
  }

  /**
   * Dorong AABB (kotak crate) keluar dari collider statis, per sumbu
   * penetrasi terkecil. Ramps dilewati bila kaki sudah di atas permukaan.
   */
  resolveBox(
    position: THREE.Vector3,
    halfX: number,
    halfZ: number,
    height: number,
  ): THREE.Vector3 {
    const resolved = position.clone();

    for (const c of this.colliders) {
      if (c.disabled) continue;
      if (
        resolved.x + halfX <= c.min.x ||
        resolved.x - halfX >= c.max.x ||
        resolved.z + halfZ <= c.min.z ||
        resolved.z - halfZ >= c.max.z
      ) {
        continue;
      }

      const feet = resolved.y;
      const head = resolved.y + height;

      const closestX = THREE.MathUtils.clamp(resolved.x, c.min.x, c.max.x);
      const closestZ = THREE.MathUtils.clamp(resolved.z, c.min.z, c.max.z);
      const surfaceY =
        c.kind === 'ramp'
          ? c.rampStartY + c.tiltZ * (closestZ - c.min.z) + c.tiltX * (closestX - c.min.x)
          : c.max.y;

      if (feet >= surfaceY) continue;
      // Toleransi step-up: permukaan yang cuma sedikit di atas kaki dan
      // masih di bawah kepala bisa dinaiki (ground-snap mengangkat).
      // Ini yang memungkinkan crate ditarik naik tangga/ramp.
      // Dinding tinggi tetap mendorong keluar seperti biasa.
      if (surfaceY - feet <= 0.55 && head > surfaceY) continue;
      if (head <= c.min.y) continue;

      const penMinX = resolved.x + halfX - c.min.x;
      const penMaxX = c.max.x - (resolved.x - halfX);
      const penMinZ = resolved.z + halfZ - c.min.z;
      const penMaxZ = c.max.z - (resolved.z - halfZ);
      const penX = Math.min(penMinX, penMaxX);
      const penZ = Math.min(penMinZ, penMaxZ);

      if (penX < penZ) {
        resolved.x += penMinX < penMaxX ? -penMinX : penMaxX;
      } else {
        resolved.z += penMinZ < penMaxZ ? -penMinZ : penMaxZ;
      }
    }

    return resolved;
  }
}